import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useState } from "react";
import { DropdownItem, DropdownMenu } from "reactstrap";
import { useIntl } from "react-intl";
import { LOCALE } from "../../../constants/locale.constant";
import { User } from "../../../cores/session/interfaces/models/user";
import Avatar from "../Avatar";
import { Flag } from "../Flag";
import { getFirstLetters } from "../../utils/stringUtils";
import "./UserNavbarMenu.scss";

const HELP_URL = "https://soporte.wembii.com";

type Props = {
  user: User | null | undefined;
  isDarkMode: boolean;
  userPlan: { name: string } | null | undefined;
  locale: string;
  onLogout: () => void;
  onEditUser: () => void;
  onEditIntegrations: () => void;
  goToPlans: () => void;
  onSelectLanguage: (language: string) => void;
};

export function UserNavbarMenu(props: Props) {
  const {
    user,
    isDarkMode,
    userPlan,
    locale,
    onLogout,
    onEditUser,
    onEditIntegrations,
    goToPlans,
    onSelectLanguage,
  } = props;

  const intl = useIntl();
  const [langOpen, setLangOpen] = useState(false);

  const displayName = user
    ? `${user.firstName} ${user.lastName}`.trim()
    : "";

  const onPickLanguage = useCallback(
    (code: string) => {
      onSelectLanguage(code);
      setLangOpen(false);
    },
    [onSelectLanguage]
  );

  return (
    <DropdownMenu
      right
      className={`user-navbar-menu border-0 shadow ${isDarkMode ? "user-navbar-menu--dark" : ""}`}
    >
      <div className="user-navbar-menu__header">
        <Avatar
          className="user-navbar-menu__avatar flex-shrink-0"
          backgroundColor={isDarkMode ? "#21262D" : "#f0f2f5"}
          color={isDarkMode ? "#E6EDF3" : "#24292f"}
          label={getFirstLetters(`${user?.lastName} ${user?.firstName}`)}
          image={user?.avatarUrl}
          width="48px"
          height="48px"
        />
        <div className="user-navbar-menu__meta min-width-0">
          <div className="user-navbar-menu__name text-truncate">{displayName}</div>
          <div className="user-navbar-menu__email text-truncate">
            {user?.email}
          </div>
        </div>
      </div>

      <DropdownItem divider className="user-navbar-menu__divider my-0" />

      <DropdownItem
        className="user-navbar-menu__item"
        tag="a"
        href={HELP_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="user-navbar-menu__icon" aria-hidden>
          <FontAwesomeIcon icon={["fas", "headphones"]} />
        </span>
        <span>{intl.formatMessage({ id: "app.navbar.userMenu.help" })}</span>
      </DropdownItem>

      <DropdownItem
        toggle={false}
        className="user-navbar-menu__item"
        onClick={(e) => {
          e.preventDefault();
          setLangOpen((v) => !v);
        }}
      >
        <span className="user-navbar-menu__icon" aria-hidden>
          <FontAwesomeIcon icon={["fas", "globe"]} />
        </span>
        <span className="flex-grow-1 text-left">
          {intl.formatMessage({ id: "app.navbar.userMenu.language" })}
        </span>
        <FontAwesomeIcon
          className="user-navbar-menu__chevron"
          icon={["fas", langOpen ? "chevron-down" : "chevron-right"]}
        />
      </DropdownItem>

      {langOpen && (
        <div className="user-navbar-menu__lang-nested">
          <DropdownItem
            className="user-navbar-menu__item user-navbar-menu__item--nested"
            onClick={() => onPickLanguage(LOCALE.SPANISH)}
          >
            <div className="d-flex align-items-center w-100">
              <Flag flag="es" width={18} />
              <span className="ml-2">Español</span>
              {locale === LOCALE.SPANISH && (
                <FontAwesomeIcon
                  icon={["fas", "check"]}
                  className="user-navbar-menu__check ml-auto pl-2"
                />
              )}
            </div>
          </DropdownItem>
          <DropdownItem
            className="user-navbar-menu__item user-navbar-menu__item--nested"
            onClick={() => onPickLanguage(LOCALE.ENGLISH)}
          >
            <div className="d-flex align-items-center w-100">
              <Flag flag="en" width={18} />
              <span className="ml-2">English</span>
              {locale === LOCALE.ENGLISH && (
                <FontAwesomeIcon
                  icon={["fas", "check"]}
                  className="user-navbar-menu__check ml-auto pl-2"
                />
              )}
            </div>
          </DropdownItem>
          <DropdownItem
            className="user-navbar-menu__item user-navbar-menu__item--nested"
            onClick={() => onPickLanguage(LOCALE.PORTUGUESE)}
          >
            <div className="d-flex align-items-center w-100">
              <Flag flag="pt" width={18} />
              <span className="ml-2">Portugués</span>
              {locale === LOCALE.PORTUGUESE && (
                <FontAwesomeIcon
                  icon={["fas", "check"]}
                  className="user-navbar-menu__check ml-auto pl-2"
                />
              )}
            </div>
          </DropdownItem>
        </div>
      )}

      <DropdownItem
        className="user-navbar-menu__item"
        onClick={onEditIntegrations}
      >
        <span className="user-navbar-menu__icon" aria-hidden>
          <FontAwesomeIcon icon={["fas", "puzzle-piece"]} />
        </span>
        <span>{intl.formatMessage({ id: "app.navbar.integrations" })}</span>
      </DropdownItem>

      {userPlan && (
        <DropdownItem className="user-navbar-menu__item" onClick={goToPlans}>
          <span className="user-navbar-menu__icon" aria-hidden>
            <FontAwesomeIcon icon={["fas", "crown"]} />
          </span>
          <span>{intl.formatMessage({ id: "app.navbar.subscription" })}</span>
        </DropdownItem>
      )}

      <DropdownItem className="user-navbar-menu__item" onClick={onEditUser}>
        <span className="user-navbar-menu__icon" aria-hidden>
          <FontAwesomeIcon icon={["fas", "user"]} />
        </span>
        <span>
          {intl.formatMessage({ id: "app.navbar.userMenu.accountSettings" })}
        </span>
      </DropdownItem>

      <DropdownItem divider className="user-navbar-menu__divider my-0" />

      <DropdownItem className="user-navbar-menu__item" onClick={onLogout}>
        <span className="user-navbar-menu__icon" aria-hidden>
          <FontAwesomeIcon icon={["fas", "sign-out-alt"]} />
        </span>
        <span>{intl.formatMessage({ id: "app.navbar.onLogout" })}</span>
      </DropdownItem>
    </DropdownMenu>
  );
}
